import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import BreadCrumbs from "../components/Breadcrumbs";
import {useParams} from "react-router-dom";
import {observer} from "mobx-react-lite";
import Container from "@mui/material/Container";
import {Context} from "..";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import {SERVERS_ROUTE} from "../utils/consts";
import {fetchServerLogsAPI, sendRcon} from "../http/ServerAPI";
import {getApiErrorMessage} from "../http/apiError";
import {useSnackbar} from "../components/snackbar/SnackbarProvider";

const decodeHtmlEntities = (value) => {
    if (typeof value !== 'string') {
        return value;
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
};

const normalizeLogResponse = (response, serverId) => {
    // fetchServerLogsAPI уже возвращает тело ответа, но оставляем поддержку axios-response
    // на случай прямого использования $authHost в будущем.
    const payload = response?.message ? response : response?.data || response || {};
    const logs = Array.isArray(payload)
        ? payload
        : payload?.logs || payload?.data || [];

    return {
        log: Number(payload?.server_id || serverId),
        data: Array.isArray(logs)
            ? logs.map((item) => ({
                ...item,
                message: decodeHtmlEntities(item?.message || ''),
            }))
            : [],
    };
};

const formatLogDate = (date) => {
    if (!date) {
        return '';
    }

    const normalized = typeof date === 'string'
        ? date.replace(' ', 'T')
        : date;

    const parsed = new Date(normalized);

    if (Number.isNaN(parsed.getTime())) {
        return String(date);
    }

    return parsed.toLocaleString();
};

const Log = observer(() => {
    const {server_id} = useParams();
    const numericServerId = useMemo(() => Number(server_id), [server_id]);

    const {user} = useContext(Context);
    const snackbar = useSnackbar();

    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [message, setMessage] = useState("");

    const isPaused = user.isLogPaused(numericServerId);

    const hrefs = [
        { href: SERVERS_ROUTE, name: "Список серверов" },
    ];

    const chatEndRef = useRef(null);

    const loadInitialLogs = useCallback(async ({silent = false} = {}) => {
        if (!numericServerId) {
            return;
        }

        user.clearLog(numericServerId);
        setChatMessages([]);

        if (!silent) {
            setIsLoadingLogs(true);
        }

        try {
            const response = await fetchServerLogsAPI(numericServerId);
            const storage = normalizeLogResponse(response, numericServerId);

            user.replaceLog(storage);
            setChatMessages(storage.data);

            if (!silent) {
                snackbar.success('История логов загружена');
            }
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Не удалось загрузить историю логов'));
        } finally {
            if (!silent) {
                setIsLoadingLogs(false);
            }
        }
    }, [numericServerId, snackbar, user]);

    useEffect(() => {
        if (user.userName) {
            setName(user.userName);
            setRole(user.role);
        } else {
            setName('');
            setRole('');
        }
    }, [user.userName, user.role]);

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            if (!isMounted) {
                return;
            }

            await loadInitialLogs({silent: true});

            if (isMounted) {
                setIsLoadingLogs(false);
            }
        };

        if (numericServerId) {
            setIsLoadingLogs(true);
            user.resumeLog(numericServerId);
            load();
        }

        return () => {
            isMounted = false;
        };
    }, [numericServerId, loadInitialLogs, user]);

    useEffect(() => {
        if (isPaused) {
            return;
        }

        const logData = user.getStorageByLogId(numericServerId);
        setChatMessages(logData.data);
    }, [user.log, numericServerId, user, isPaused]);

    useEffect(() => {
        if (isPaused) {
            return;
        }

        const scrollToBottom = () => {
            if (chatEndRef.current) {
                chatEndRef.current.scrollIntoView({ behavior: "smooth" });
            }
        };

        scrollToBottom();
        const timeoutId = setTimeout(scrollToBottom, 100);

        return () => clearTimeout(timeoutId);
    }, [chatMessages, isPaused]);

    const handlePause = () => {
        user.pauseLog(numericServerId);
        snackbar.warning('Приём новых WSS-логов приостановлен');
    };

    const handleResume = async () => {
        user.resumeLog(numericServerId);
        await loadInitialLogs({silent: false});
        snackbar.success('Приём WSS-логов возобновлён');
    };

    const handleRefresh = async () => {
        await loadInitialLogs({silent: false});
    };

    const sendMessage = async (serverId, command) => {
        try {
            await sendRcon(serverId, command, 'command');
            snackbar.success('Команда отправлена');
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Не удалось отправить команду'));
        }
    };

    const handleSendMessage = () => {
        const command = message.trim();

        if (command) {
            sendMessage(numericServerId, command);
            setMessage("");
        }
    };

    return (
        <Container
            maxWidth="xl"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 70px)',
                py: 2,
            }}
        >
            <Box sx={{ width: '100%', mb: 2 }}>
                <BreadCrumbs hrefs={hrefs} name='Логи сервера' />
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 2,
                    border: '1px solid rgba(255,255,255,0.08)',
                    background: 'linear-gradient(135deg, rgba(30,35,39,0.98), rgba(18,21,24,0.98))',
                    borderRadius: 3,
                }}
            >
                <Stack
                    direction={{xs: 'column', md: 'row'}}
                    justifyContent="space-between"
                    alignItems={{xs: 'flex-start', md: 'center'}}
                    spacing={2}
                >
                    <Box>
                        <Typography variant="h4" sx={{fontWeight: 700, mb: 0.5}}>
                            Логи сервера #{server_id}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                            label={isPaused ? 'Пауза' : 'Live'}
                            color={isPaused ? 'warning' : 'success'}
                            variant={isPaused ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label={`${chatMessages.length} записей`}
                            variant="outlined"
                        />
                        {role && (
                            <Chip
                                label={name ? `${name} · ${role}` : role}
                                variant="outlined"
                            />
                        )}
                    </Stack>
                </Stack>

                <Divider sx={{my: 2}} />

                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
                    {isPaused ? (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleResume}
                            disabled={isLoadingLogs}
                        >
                            Старт
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handlePause}
                        >
                            Пауза
                        </Button>
                    )}

                    <Tooltip title="Очистить текущий список и заново загрузить последние записи через API">
                        <span>
                            <Button
                                variant="outlined"
                                onClick={handleRefresh}
                                disabled={isLoadingLogs}
                            >
                                Обновить историю
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Paper>

            {isPaused && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 2,
                        border: '1px solid rgba(255,193,7,0.35)',
                        backgroundColor: 'rgba(255,193,7,0.08)',
                    }}
                >
                    <Typography variant="body2" color="warning.main">
                        Пауза включена: новые WSS-логи временно игнорируются. Нажми «Старт», чтобы заново загрузить историю через API и продолжить live-приём.
                    </Typography>
                </Paper>
            )}

            <Paper
                elevation={0}
                sx={{
                    flexGrow: 1,
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 3,
                    backgroundColor: '#0f1317',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.25,
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Typography variant="subtitle2" color="text.secondary">
                        Консоль событий
                    </Typography>
                    {isLoadingLogs && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={16} />
                            <Typography variant="caption" color="text.secondary">
                                загрузка...
                            </Typography>
                        </Stack>
                    )}
                </Box>

                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        px: 2,
                        py: 1.5,
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        fontSize: 13,
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#4f4f4f',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: '#666',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#111',
                        }
                    }}
                >
                    {isLoadingLogs && chatMessages.length === 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                            <CircularProgress size={22} />
                            <Typography>Загружаем последние записи лога...</Typography>
                        </Box>
                    ) : chatMessages && chatMessages.length > 0 ? (
                        <Box component="ul" sx={{ listStyleType: 'none', p: 0, m: 0 }}>
                            {chatMessages.map((item, index) => (
                                <Box
                                    component="li"
                                    key={`${item.date || 'log'}-${index}`}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {xs: '1fr', md: '180px 1fr'},
                                        gap: {xs: 0.5, md: 1.5},
                                        alignItems: 'start',
                                        py: 0.75,
                                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.025)',
                                        },
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: 'text.secondary',
                                            fontFamily: 'inherit',
                                            fontSize: 12,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {formatLogDate(item.date)}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: '#d6dde3',
                                            fontFamily: 'inherit',
                                            fontSize: 13,
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {item.message}
                                    </Typography>
                                </Box>
                            ))}
                            <div ref={chatEndRef} />
                        </Box>
                    ) : (
                        <Box sx={{py: 4, textAlign: 'center'}}>
                            <Typography color="text.secondary">
                                Сообщений пока нет
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    mt: 1.5,
                    p: 1.25,
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: 'rgb(30, 35, 37)',
                }}
            >
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Введите RCON-команду"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && message.trim()) {
                                handleSendMessage();
                            }
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!message.trim()}
                        sx={{minWidth: 130}}
                    >
                        Отправить
                    </Button>
                </Stack>
            </Paper>
        </Container>
    );
});

export default Log;
